import React, { useMemo } from 'react';
import { getCategoryIconUrl } from './CategoryIcons';
import { VENUE_CATEGORIES } from '../../utils/venueCategories';
import { distanceKmFromUser } from './distance';
import { hapticTap } from '../../utils/appFeedback';

// Chips are only rendered for categories present on at least one venue
// within this radius of the guest. Matches the map's NEARBY_RADIUS_KM.
const CHIP_RADIUS_KM = 20;

/**
 * Category chip strip on the guest map.
 *
 * - Shows one chip per canonical category that at least one venue
 *   within 20 km has selected. Categories no venue nearby offers are
 *   hidden entirely so the strip never lies.
 * - When the guest has no live location yet, every category present on
 *   any loaded venue is shown as a fallback.
 * - Single-select: tapping the active chip clears it.
 * - Selection filters the "All around you" list + map pins but never
 *   the "Closest to you" or "Events" carousels (owned by the parent).
 */
export function GuestMapCategoryChips({ selected, onSelect, venues = [], userPosition = null }) {
  const availableSlugs = useMemo(() => {
    const set = new Set();
    (venues || []).forEach((v) => {
      const cats = Array.isArray(v?.categories) ? v.categories : [];
      if (cats.length === 0) return;
      if (userPosition) {
        const d = distanceKmFromUser(v, userPosition);
        if (!Number.isFinite(d) || d > CHIP_RADIUS_KM) return;
      }
      cats.forEach((slug) => {
        if (slug) set.add(slug);
      });
    });
    return set;
  }, [venues, userPosition]);

  const visibleChips = useMemo(
    () => VENUE_CATEGORIES.filter((cat) => availableSlugs.has(cat.slug)),
    [availableSlugs],
  );

  // When no venue nearby has any recognised category, we still render an
  // empty flex row so the layout height stays identical (the sticky
  // header measurement in GuestMapPage relies on a stable bottom edge).
  if (visibleChips.length === 0) {
    return <div data-testid="map-category-chips-empty" style={{ height: 46, flexShrink: 0 }} />;
  }

  return (
    <div
      data-testid="map-category-chips"
      style={{ display: 'flex', gap: 8, padding: '4px 16px 10px', overflowX: 'auto', flexShrink: 0 }}
    >
      {visibleChips.map(({ slug, display }) => {
        const iconUrl = getCategoryIconUrl(slug);
        const isActive = selected === slug;
        return (
          <button
            key={slug}
            type="button"
            data-testid={`map-category-${slug}`}
            aria-pressed={isActive}
            onClick={() => {
              hapticTap();
              onSelect(isActive ? null : slug);
            }}
            className="map-category-chip"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '7px 14px 7px 10px', borderRadius: 16, whiteSpace: 'nowrap',
              fontFamily: "'Inria Serif', serif", fontSize: 13.5, fontWeight: 700,
              color: isActive ? '#FFFFFF' : 'var(--text-2)',
              background: isActive ? 'var(--accent-purple)' : 'var(--surface)',
              border: isActive ? '1px solid var(--accent-purple)' : '1px solid var(--card-border)',
              cursor: 'pointer', flexShrink: 0,
              boxShadow: isActive ? 'var(--premium-shadow-main)' : 'none',
              transition: 'transform 160ms ease, box-shadow 200ms ease, background 160ms ease, color 160ms ease',
              transform: isActive ? 'translateY(-1px)' : 'translateY(0)',
            }}
          >
            {iconUrl ? (
              <span
                className="map-category-chip-icon"
                style={{ display: 'inline-flex', transformOrigin: '50% 60%' }}
              >
                <img
                  src={iconUrl}
                  alt=""
                  aria-hidden="true"
                  draggable="false"
                  width={30}
                  height={30}
                  style={{ width: 30, height: 30, display: 'block', objectFit: 'contain', margin: '-2px 0' }}
                />
              </span>
            ) : null}
            {display}
          </button>
        );
      })}
    </div>
  );
}
